import { Injectable } from '@angular/core';
import introJs from 'intro.js';

export interface TourStepOptions {
  element?: string; // CSS selector for the element to highlight
  intro: string; // The main content of the tooltip (HTML allowed)
  title?: string; // Optional title for the tooltip
  position?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-middle-aligned';
  disableInteraction?: boolean;
}

interface Tour {
  id: string;
  steps: TourStepOptions[];
}

@Injectable({
  providedIn: 'root',
})
export class TourService {
  // Use a Map to store multiple tour configurations, keyed by a unique tour ID.
  private tours = new Map<string, Tour>();

  constructor() {}


  createOrResetTour(tourId: string): void {
    this.tours.set(tourId, { id: tourId, steps: [] });
  }

  addStep(tourId: string, step: TourStepOptions): void {
    const tour = this.tours.get(tourId);
    if (!tour) {
      console.error(`Tour with ID "${tourId}" not found. Please create it first using createOrResetTour().`);
      return;
    }
    tour.steps.push(step);
  }

  buildIntroContent(body: string, header?: string, isWelcome: boolean = false): string {
    const welcomeEmoji = isWelcome ? '<div class="wave">👋</div>' : '';
    const headerHtml = header ? `<div class="tour-line header" style="font-size: 20px; font-weight: bold; text-align: center;">${header}</div>` : '';
    const bodyHtml = `<div class="tour-line center" style="text-align: center;">${welcomeEmoji} ${body}</div>`;

    return `<div class="typing-container">${headerHtml}${bodyHtml}</div>`;
  }

  startTour(tourId: string): void {
    const tourData = this.tours.get(tourId);

    if (!tourData || tourData.steps.length === 0) {
      console.error(`Tour "${tourId}" is not defined or has no steps.`);
      return;
    }

    const introJsSteps = tourData.steps
      .map(stepConfig => {
        const element = stepConfig.element ? document.querySelector(stepConfig.element) : null;
        if (stepConfig.element && !element) {
          console.warn(`Tour element not found for selector: "${stepConfig.element}". Skipping step.`);
          return null;
        }
        return { ...stepConfig, element: element as HTMLElement | null };
      })
      .filter((step): step is NonNullable<typeof step> => step !== null);

    if (introJsSteps.length === 0) {
      console.error(`Tour "${tourId}" could not start because no valid step elements were found in the DOM.`);
      return;
    }

    const tour = introJs().setOptions({
      steps: introJsSteps,
      showProgress: false,
      showBullets: true,
      disableInteraction: true
    });

    // Use onafterchange to ensure DOM is ready
    tour.onafterchange(() => {
      setTimeout(() => {
        const currentTooltip = document.querySelector('.introjs-tooltip');
        if (currentTooltip) {
          this.addTypingEffect(currentTooltip.querySelector('.typing-container'));
        }
      }, 50);
    });

    const logincount = localStorage.getItem('login_count');
    if (Number(logincount) < 1) {
      tour.start();
    }
  }



    private addTypingEffect(container: HTMLElement | null) {
    if (!container) return;
    const typingLines = container.querySelectorAll('.typing-line, .tour-line');
    if (!typingLines.length) return;

    let currentLineIndex = 0;

    // Set a fixed height for the container to prevent size fluctuation
    const containerHeight = container.offsetHeight;
    container.style.height = `${containerHeight}px`;

    (typingLines as NodeListOf<HTMLElement>).forEach(line => {
        line.style.visibility = 'hidden'; // Hide all lines initially
    });

    const typeLine = (line: HTMLElement) => {
        const originalHTML = line.innerHTML;
        line.innerHTML = ''; // Empty content for animation
        line.style.visibility = 'visible';

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = originalHTML;

        const chars: (string | Node)[] = [];
        
        // Extract text and keep HTML tags intact
        tempDiv.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                chars.push(...node.textContent!.split("")); // Split text into characters
            } else {
                chars.push(node.cloneNode(true)); // Keep HTML tags intact
            }
        });

        let charIndex = 0;

        const type = () => {
            if (charIndex < chars.length) {
                if (typeof chars[charIndex] === "string") {
                    line.innerHTML += chars[charIndex]; // Append character
                } else {
                    line.appendChild(chars[charIndex] as Node); // Append HTML node
                }
                charIndex++;
                setTimeout(type, 30); // Adjust speed
            } else {
                currentLineIndex++;
                if (currentLineIndex < typingLines.length) {
                    setTimeout(() => typeLine(typingLines[currentLineIndex] as HTMLElement), 300);
                }
            }
        };

        type();
    };

    // Start animation
    setTimeout(() => typeLine(typingLines[0] as HTMLElement), 300);
  }
}